import { FC, ReactNode, UIEvent, useCallback, useMemo, useState } from "react";
import { gql } from "@apollo/client";
import { cloneDeep, debounce } from "lodash";
import Avatar from "@src/lib/ui/Avatar";
import Text from "@src/lib/ui/Text";
import useHiddenLocationGroupTypes from "@src/features/home/hooks/useHiddenLocationGroupTypes";
import {
  LocationGroupType,
  UserSelectionInput,
  UserType,
} from "@src/types.generated";
import { cn } from "@src/lib/utils/cn";
import Pill from "@src/lib/ui/Pill";
import useLocationGroups from "@src/features/locations/hooks/useLocationGroups";
import useLocations from "@src/features/locations/hooks/useLocations";
import useRoles from "@src/features/roles/hooks/useRoles";

import Fuse from "fuse.js";
import {
  isUserSelectionInputEmpty,
  isUserSelectionInputLocationOnly,
} from "@src/features/contentLibrary/utils/assignments";
import UserSelectionPreview from "./UserSelectionPreview";
import { isLocationGroupType, toCamelCase } from "./utils";
import {
  SelectUsersDialog_UserThinFragment,
  useSelectUsersDialog_UsersQuery,
} from "./SelectUsersDialog.generated";
import { LocationGroupThinFragment } from "@src/features/locations/hooks/useLocationGroups.generated";
import { LocationThinFragment } from "@src/features/locations/hooks/useLocations.generated";
import { RoleThinFragment } from "@src/features/roles/hooks/useRoles.generated";
import useUserSelections from "./hooks/useUserSelections";
import useGetUsersForOneOffAssignment from "./hooks/useGetUsersForOneOffAssignment";
import { pluralize } from "@src/lib/utils/strings";
import { USER_SELECTION_ICON } from "./constants";
import { Separator } from "@src/lib/ui/Separator";
import Spacer from "@src/lib/ui/Spacer";
import useUserSelectionInputState from "./hooks/useUserSelectionInputState";
import { useDebouncedInputValue } from "@src/lib/hooks/useDebouncedInputValue";
import useRoleGroups from "@src/features/roles/hooks/useRoleGroups";
import { RoleGroupThinFragment } from "@src/features/roles/hooks/useRoleGroups.generated";
import { Input } from "@src/lib/ui/Input";
import { Label } from "@src/lib/ui/Label";
import { Tooltip } from "@src/lib/ui/Tooltip";
import { TooltipTrigger } from "@src/lib/ui/Tooltip";
import { TooltipContent } from "@src/lib/ui/Tooltip";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@src/lib/ui/Dialog";
import { Button } from "@src/lib/ui/Button";
import PlusIcon from "@src/lib/icons/18px/plus";
import UpdateAccessMultiSelectConfirmationDialog from "../UpdateAccessMultiSelectConfirmationDialog";
import { DIALOG_Z_INDEX_CLASSNAMES, DialogZIndex } from "./dialogZIndex";

const PAGINATION_LIMIT = 30;
const FUSE_CONFIG = { keys: ["name"], threshold: 0.3, minMatchCharLength: 2 };

export type UserSelectionFilterType =
  | "locations"
  | "roles"
  | "departments"
  | "people"
  | LocationGroupType
  | "hireDate";
export const USER_SELECTION_FILTERS: UserSelectionFilterType[] = [
  "roles",
  "departments",
  "locations",
  LocationGroupType.Region,
  LocationGroupType.Brand,
  LocationGroupType.Franchise,
  "hireDate",
  "people",
];

type Props = {
  onSelect: (input: UserSelectionInput) => void;
  input?: UserSelectionInput;
  showSelectedUsersCount?: boolean;
  title?: string;
  hideFilters?: UserSelectionFilterType[];
  defaultFilter?: UserSelectionFilterType;
  showUserTypeOptions?: boolean;
  hideUserTypes?: UserType[];
  lockedUserTypes?: UserType[];
  pillColor?: string;
  children?: React.ReactNode;
  infoBanner?: React.ReactNode;
  description?: string;
  emptyStateSubtitle?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItemsCount?: number;
  showConfirmationDialog?: boolean;
  useLocationSelectionCopy?: boolean;
  requireSelection?: boolean;
  zIndex?: DialogZIndex;
  /** Anchored at the footer's start, away from Cancel and confirm — a delete, typically. */
  footerStart?: React.ReactNode;
};

const SelectUsersDialog: FC<Props> = ({
  pillColor = "blue",
  showSelectedUsersCount = true,
  title = "Select users",
  showUserTypeOptions = false,
  hideUserTypes,
  lockedUserTypes,
  children,
  description,
  emptyStateSubtitle,
  infoBanner,
  open,
  onOpenChange,
  selectedItemsCount,
  showConfirmationDialog = false,
  useLocationSelectionCopy = false,
  requireSelection = false,
  ...props
}) => {
  const { userSelectionInput, setUserSelectionInput } =
    useUserSelectionInputState({
      input: props.input,
      allowUserTypes: showUserTypeOptions,
    });
  const usersSelectedCount = useGetUsersForOneOffAssignment(
    userSelectionInput,
    { skip: !showSelectedUsersCount },
  );
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  // A selection with locations but no user types, roles, or departments
  // matches no one on the backend (`users | (locations & roles)`), so saving
  // it would silently grant access to zero people. Dialogs that offer no way
  // to add an audience (trainee posts show only the locations filter, and the
  // backend accepts their own-location selections) are exempt.
  const canAddAudience =
    !props.hideFilters?.includes("roles") ||
    !props.hideFilters?.includes("departments");
  const selectionMatchesNoOne = useMemo(() => {
    return (
      showUserTypeOptions &&
      !useLocationSelectionCopy &&
      canAddAudience &&
      isUserSelectionInputLocationOnly(userSelectionInput)
    );
  }, [
    showUserTypeOptions,
    useLocationSelectionCopy,
    canAddAudience,
    userSelectionInput,
  ]);

  const handleConfirm = useCallback(() => {
    if (showConfirmationDialog) {
      setConfirmationOpen(true);
    } else {
      props.onSelect(userSelectionInput);
      onOpenChange(false);
    }
  }, [showConfirmationDialog, props, userSelectionInput, onOpenChange]);

  const confirmButtonLabel = useMemo(() => {
    if (useLocationSelectionCopy) return "Select locations";
    if (showSelectedUsersCount) {
      return `Select ${usersSelectedCount.length} ${pluralize("user", usersSelectedCount.length)}`;
    }
    return "Select";
  }, [
    useLocationSelectionCopy,
    showSelectedUsersCount,
    usersSelectedCount.length,
  ]);
  const zIndex = props.zIndex
    ? DIALOG_Z_INDEX_CLASSNAMES[props.zIndex]
    : undefined;
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {children && <DialogTrigger asChild>{children}</DialogTrigger>}
        <DialogContent
          className={cn(
            "flex h-[600px] max-h-[80vh] max-w-[1200px] flex-col gap-0 overflow-hidden p-0",
            zIndex,
          )}
          data-testid="select-users-dialog"
        >
          <DialogHeader className="shrink-0 border-b border-border p-4">
            <DialogTitle>{title}</DialogTitle>
            {description && (
              <DialogDescription>{description}</DialogDescription>
            )}
          </DialogHeader>
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <LeftSidePanel
              input={userSelectionInput}
              showUserTypeOptions={showUserTypeOptions}
              setInput={setUserSelectionInput}
              hideFilters={props.hideFilters}
              defaultFilter={props.defaultFilter}
              hideUserTypes={hideUserTypes}
            />
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-1">
              <UserSelectionPreview
                pillColor={pillColor}
                input={userSelectionInput}
                showUserTypeOptions={showUserTypeOptions}
                setInput={setUserSelectionInput}
                emptyStateSubtitle={emptyStateSubtitle}
                useLocationSelectionCopy={useLocationSelectionCopy}
                lockedUserTypes={lockedUserTypes}
              />
            </div>
          </div>
          {infoBanner && <div className="shrink-0">{infoBanner}</div>}
          {selectionMatchesNoOne && (
            <div
              className="shrink-0 border-t border-status-warning-border bg-status-warning-bg px-4 py-2"
              data-testid="location-only-selection-warning"
            >
              <Text
                variant="fine-print-title"
                className="text-status-warning-text"
                multiline
              >
                Locations alone don&apos;t select anyone. Add at least one user
                type, role, or department for these locations.
              </Text>
            </div>
          )}
          <DialogFooter className="shrink-0 items-center justify-between border-t border-border p-4 sm:justify-between">
            <div className="flex items-center">{props.footerStart}</div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={
                  selectionMatchesNoOne ||
                  (requireSelection &&
                    (isUserSelectionInputEmpty(userSelectionInput) ||
                      usersSelectedCount.length === 0))
                }
                data-testid="confirm-button"
              >
                {confirmButtonLabel}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmationOpen && (
        <UpdateAccessMultiSelectConfirmationDialog
          open={confirmationOpen}
          onOpenChange={setConfirmationOpen}
          onConfirm={() => {
            props.onSelect(userSelectionInput);
            onOpenChange(false);
          }}
          selectedItemsCount={selectedItemsCount || 1}
        />
      )}
    </>
  );
};

type LeftSidePanelProps = {
  input: UserSelectionInput;
  setInput: (input: UserSelectionInput) => void;
  showUserTypeOptions?: boolean;
  hideFilters?: UserSelectionFilterType[];
  defaultFilter?: UserSelectionFilterType;
  hideUserTypes?: UserType[];
};
const LeftSidePanel: FC<LeftSidePanelProps> = (props) => {
  const hiddenFilters = useHiddenLocationGroupTypes();
  const visibleFilters = useMemo(
    () =>
      USER_SELECTION_FILTERS.filter(
        (filter) => !hiddenFilters.includes(filter as LocationGroupType),
      ).filter((filter) => !props.hideFilters?.includes(filter)),
    [hiddenFilters, props.hideFilters],
  );
  const allUserTypeFiltersAreSelected = useMemo(() => {
    return (
      props.input.allTrainees &&
      props.input.allManagers &&
      props.input.allAdmins
    );
  }, [props.input.allAdmins, props.input.allManagers, props.input.allTrainees]);
  const {
    selectedLocationGroups,
    selectedRoles,
    selectedRoleGroups,
    selectedLocations,
    selectedUsers,
  } = useUserSelections(props.input);
  const {
    value: search,
    setValue: setSearch,
    debouncedValue: debouncedSearch,
  } = useDebouncedInputValue({ initialValue: "", delay: 300 });
  const [selectedFilter, setSelectedFilter] = useState<UserSelectionFilterType>(
    props.defaultFilter && visibleFilters.includes(props.defaultFilter)
      ? props.defaultFilter
      : visibleFilters[0],
  );
  const { locationGroups } = useLocationGroups();
  const { locations } = useLocations();
  const { roleGroups } = useRoleGroups();
  const { roles } = useRoles();
  const getPeopleQueryInput = useMemo(() => {
    return {
      search:
        debouncedSearch.length > 0 ? { value: debouncedSearch } : undefined,
      sort: { column: "name", descending: false },
    };
  }, [debouncedSearch]);
  const { data: usersData, fetchMore } = useSelectUsersDialog_UsersQuery({
    variables: {
      input: getPeopleQueryInput,
      pagination: {
        limit: PAGINATION_LIMIT,
        offset: 0,
      },
    },
  });
  const totalUsers = useMemo(() => {
    return usersData?.People.totalCount || 0;
  }, [usersData?.People.totalCount]);
  const users: SelectUsersDialog_UserThinFragment[] = useMemo(() => {
    return usersData?.People.objects || [];
  }, [usersData?.People.objects]);
  const hasMoreUsers = useMemo(() => {
    return users.length < totalUsers;
  }, [users, totalUsers]);
  const visibleUserSelectionFilterOptions = useMemo(() => {
    if (selectedFilter === LocationGroupType.Brand) {
      const selectedBrands = selectedLocationGroups[LocationGroupType.Brand];
      const visibleBrands = locationGroups
        .filter((x) => !selectedBrands.includes(x))
        .filter((x) => x.type === LocationGroupType.Brand);
      if (search) {
        const fuse = new Fuse(visibleBrands, FUSE_CONFIG);
        return fuse.search(search).map((r) => r.item);
      }
      return visibleBrands;
    } else if (selectedFilter === LocationGroupType.Franchise) {
      const selectedFranchises =
        selectedLocationGroups[LocationGroupType.Franchise];
      const visibleFranchises = locationGroups
        .filter((x) => !selectedFranchises.includes(x))
        .filter((x) => x.type === LocationGroupType.Franchise);
      if (search) {
        const fuse = new Fuse(visibleFranchises, FUSE_CONFIG);
        return fuse.search(search).map((r) => r.item);
      }
      return visibleFranchises;
    } else if (selectedFilter === LocationGroupType.Region) {
      const selectedRegions = selectedLocationGroups[LocationGroupType.Region];
      const visibleRegions = locationGroups
        .filter((x) => !selectedRegions.includes(x))
        .filter((x) => x.type === LocationGroupType.Region);
      if (search) {
        const fuse = new Fuse(visibleRegions, FUSE_CONFIG);
        return fuse.search(search).map((r) => r.item);
      }
      return visibleRegions;
    } else if (selectedFilter === "locations") {
      const visibleLocations = locations.filter(
        (x) => !selectedLocations.includes(x),
      );
      if (search) {
        const fuse = new Fuse(visibleLocations, FUSE_CONFIG);
        return fuse.search(search).map((r) => r.item);
      }
      return visibleLocations;
    } else if (selectedFilter === "roles") {
      const visibleRoles = roles.filter((x) => !selectedRoles.includes(x));
      if (search) {
        const fuse = new Fuse(visibleRoles, FUSE_CONFIG);
        return fuse.search(search).map((r) => r.item);
      }
      return visibleRoles;
    } else if (selectedFilter === "departments") {
      const visibleRoleGroups = roleGroups.filter(
        (x) => !selectedRoleGroups.includes(x),
      );
      if (search) {
        const fuse = new Fuse(visibleRoleGroups, FUSE_CONFIG);
        return fuse.search(search).map((r) => r.item);
      }
      return visibleRoleGroups;
    } else if (selectedFilter === "people") {
      return users.filter((x) => !selectedUsers.some((y) => y.id === x.id));
    } else {
      return [];
    }
  }, [
    selectedFilter,
    selectedLocationGroups,
    locationGroups,
    search,
    locations,
    selectedLocations,
    roles,
    selectedRoles,
    roleGroups,
    selectedRoleGroups,
    users,
    selectedUsers,
  ]);
  const searchPlaceholder = useMemo(() => {
    if (selectedFilter === "hireDate") {
      return "Search...";
    }
    return `Search ${toCamelCase(selectedFilter as string)}...`;
  }, [selectedFilter]);
  const debouncedFetchMore = debounce(() => {
    fetchMore({
      variables: {
        pagination: {
          limit: PAGINATION_LIMIT,
          offset: users.length,
        },
      },
    });
  });
  const handleScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      const maxScrollValue = target.scrollHeight - target.clientHeight;
      const scrolledPastThreshold = target.scrollTop > maxScrollValue * 0.7;
      if (
        scrolledPastThreshold &&
        selectedFilter === "people" &&
        hasMoreUsers
      ) {
        debouncedFetchMore();
      }
    },
    [selectedFilter, hasMoreUsers, debouncedFetchMore],
  );
  const noUserTypesRoleGroupsOrRolesSelected = useMemo(() => {
    return (
      !props.input.allTrainees &&
      !props.input.allManagers &&
      !props.input.allAdmins &&
      !props.input.roleIds?.length &&
      !props.input.roleGroupIds?.length
    );
  }, [
    props.input.allAdmins,
    props.input.allManagers,
    props.input.allTrainees,
    props.input.roleGroupIds?.length,
    props.input.roleIds?.length,
  ]);
  const allUserTypesSelected = useMemo(() => {
    return !!(
      props.input.allTrainees &&
      props.input.allManagers &&
      props.input.allAdmins
    );
  }, [props.input.allAdmins, props.input.allManagers, props.input.allTrainees]);
  const addUserSelectionFilterOption = useCallback(
    (optionId: number | string) => {
      const newInput = cloneDeep(props.input);
      if (selectedFilter === "locations") {
        const oldLocationIds = newInput.locationIds || [];
        newInput.locationIds = [...oldLocationIds, optionId as number];
        if (noUserTypesRoleGroupsOrRolesSelected) {
          newInput.allTrainees = true;
          newInput.allManagers = true;
          newInput.allAdmins = true;
        }
      } else if (selectedFilter === "roles") {
        const oldRoleIds = newInput.roleIds || [];
        newInput.roleIds = [...oldRoleIds, optionId as number];
        if (allUserTypesSelected) {
          newInput.allTrainees = false;
          newInput.allManagers = false;
          newInput.allAdmins = false;
        }
      } else if (selectedFilter === "departments") {
        const oldRoleGroupIds = newInput.roleGroupIds || [];
        newInput.roleGroupIds = [...oldRoleGroupIds, optionId as string];
        if (allUserTypesSelected) {
          newInput.allTrainees = false;
          newInput.allManagers = false;
          newInput.allAdmins = false;
        }
      } else if (selectedFilter === "people") {
        const oldUserIds = newInput.userIds || [];
        newInput.userIds = [...oldUserIds, optionId as number];
      } else if (isLocationGroupType(selectedFilter)) {
        const oldLocationGroupIds = newInput.locationGroupIds || [];
        newInput.locationGroupIds = [
          ...oldLocationGroupIds,
          optionId as string,
        ];
        if (noUserTypesRoleGroupsOrRolesSelected) {
          newInput.allTrainees = true;
          newInput.allManagers = true;
          newInput.allAdmins = true;
        }
      }
      props.setInput(newInput);
    },
    [
      allUserTypesSelected,
      noUserTypesRoleGroupsOrRolesSelected,
      props,
      selectedFilter,
    ],
  );

  return (
    <div className="flex min-h-0 w-[320px] flex-col overflow-hidden border-r border-gray-alpha-5">
      <div className="border-b border-gray-alpha-5 p-3">
        <div className="relative">
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={selectedFilter === "hireDate"}
          />
        </div>
        <div
          className="mt-2 flex flex-wrap gap-2"
          data-testid="user-selection-filters"
        >
          {visibleFilters.map((filter) => {
            const title = toCamelCase(filter as string);
            const color = filter === selectedFilter ? "blue" : "gray";
            return (
              <Pill
                key={title}
                title={title}
                color={color}
                className="px-2.5 py-1"
                onClick={() => {
                  if (filter !== selectedFilter) {
                    setSearch("");
                  }
                  setSelectedFilter(filter);
                }}
              />
            );
          })}
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          className="flex min-h-0 flex-1 flex-col overflow-auto py-1"
          onScroll={handleScroll}
        >
          {props.showUserTypeOptions && selectedFilter === "roles" && (
            <div className="flex flex-col">
              {(!props.input.allTrainees || allUserTypesSelected) &&
                !props.hideUserTypes?.includes(UserType.Employee) && (
                  <FilterOption
                    option={{ id: -1, name: "All Trainees" }}
                    onClick={() => {
                      const newInput = cloneDeep(props.input);
                      if (
                        newInput.allTrainees &&
                        newInput.allManagers &&
                        newInput.allAdmins
                      ) {
                        newInput.roleIds = [];
                        newInput.allManagers = false;
                        newInput.allAdmins = false;
                      }
                      newInput.allTrainees = true;
                      if (
                        newInput.allTrainees &&
                        newInput.allManagers &&
                        newInput.allAdmins
                      ) {
                        newInput.roleIds = [];
                      }
                      props.setInput(newInput);
                    }}
                    showSubtitles={false}
                    icon={USER_SELECTION_ICON["roles"]}
                  />
                )}
              {(!props.input.allManagers || allUserTypesSelected) &&
                !props.hideUserTypes?.includes(UserType.Manager) && (
                  <FilterOption
                    option={{ id: -2, name: "All Managers" }}
                    onClick={() => {
                      const newInput = cloneDeep(props.input);
                      if (
                        newInput.allTrainees &&
                        newInput.allManagers &&
                        newInput.allAdmins
                      ) {
                        newInput.roleIds = [];
                        newInput.allAdmins = false;
                        newInput.allTrainees = false;
                      }
                      newInput.allManagers = true;
                      if (
                        newInput.allTrainees &&
                        newInput.allManagers &&
                        newInput.allAdmins
                      ) {
                        newInput.roleIds = [];
                      }
                      props.setInput(newInput);
                    }}
                    showSubtitles={false}
                    icon={USER_SELECTION_ICON["roles"]}
                  />
                )}
              {(!props.input.allAdmins || allUserTypesSelected) &&
                !props.hideUserTypes?.includes(UserType.Admin) && (
                  <FilterOption
                    option={{ id: -3, name: "All Admins" }}
                    onClick={() => {
                      const newInput = cloneDeep(props.input);
                      if (
                        newInput.allTrainees &&
                        newInput.allManagers &&
                        newInput.allAdmins
                      ) {
                        newInput.roleIds = [];
                        newInput.allManagers = false;
                        newInput.allTrainees = false;
                      }
                      newInput.allAdmins = true;
                      if (
                        newInput.allTrainees &&
                        newInput.allManagers &&
                        newInput.allAdmins
                      ) {
                        newInput.roleIds = [];
                      }
                      props.setInput(newInput);
                    }}
                    showSubtitles={false}
                    icon={USER_SELECTION_ICON["roles"]}
                  />
                )}
              {(!allUserTypeFiltersAreSelected || allUserTypesSelected) && (
                <>
                  <Spacer size={8} />
                  <Separator />
                  <Spacer size={8} />
                </>
              )}
            </div>
          )}
          {visibleUserSelectionFilterOptions.map((option) => {
            return (
              <FilterOption
                key={option.id}
                option={option}
                onClick={addUserSelectionFilterOption}
                showSubtitles={selectedFilter === "people"}
                icon={USER_SELECTION_ICON[selectedFilter]}
              />
            );
          })}
          {selectedFilter === "hireDate" && (
            <div className="gap-1 px-2">
              <Tooltip>
                <TooltipTrigger>
                  <Label className="mb-2 flex items-center gap-1">
                    Days after hire
                    <QuestionMarkCircledIcon className="size-4 text-icon-tertiary" />
                  </Label>
                </TooltipTrigger>
                <TooltipContent>
                  Filter users who have been at your company for at least a
                  certain number of days. We use hire date from your HRIS or the
                  date the user was added to Opus.
                </TooltipContent>
              </Tooltip>
              <Input
                type="number"
                placeholder=""
                value={props.input.daysAfterHire || ""}
                onChange={(e) => {
                  props.setInput({
                    ...props.input,
                    daysAfterHire: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  });
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type FilterOptionProps = {
  option:
    | SelectUsersDialog_UserThinFragment
    | LocationGroupThinFragment
    | LocationThinFragment
    | RoleThinFragment
    | RoleGroupThinFragment;
  showSubtitles: boolean;
  icon: ReactNode;
  onClick: (id: string | number) => void;
};
const FilterOption: FC<FilterOptionProps> = (props) => {
  const subtitle1 =
    "roles" in props.option && props.option.roles.length > 0
      ? `${props.option.roles[0].name}`
      : "No roles";
  const subtitle2 =
    "locations" in props.option && props.option.locations.length > 0
      ? `${props.option.locations[0].name}`
      : "No locations";

  return (
    <div
      className="flex max-w-full cursor-pointer items-center justify-between bg-white px-3 py-1.5 transition-colors duration-[250ms] hover:bg-gray-alpha-3"
      onClick={() => {
        props.onClick(props.option.id);
      }}
      data-testid="filter-option"
    >
      <div className="flex max-w-[90%] flex-1 items-center gap-2">
        {"profileImage" in props.option ? (
          <Avatar
            person={props.option}
            size={32}
            className="cursor-[inherit]"
          />
        ) : (
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-alpha-4 text-gray-6">
            {props.icon}
          </div>
        )}
        <div className="flex w-full flex-col justify-center self-stretch truncate">
          <Text variant="compact-title" ellipsis>
            {props.option.name}
          </Text>
          {props.showSubtitles && (
            <div className="flex w-full items-center gap-1.5 truncate">
              <Text
                variant="fine-print-description"
                className="truncate text-gray-8"
              >
                {subtitle1}
              </Text>
              <div className="size-[3px] shrink-0 rounded-full bg-gray-6" />
              <Text
                variant="fine-print-description"
                className="truncate text-gray-8"
              >
                {subtitle2}
              </Text>
            </div>
          )}
        </div>
      </div>
      <PlusIcon className="size-3.5 shrink-0 text-blue-9" />
    </div>
  );
};

gql`
  query SelectUsersDialog_Users(
    $input: GetPeopleInput!
    $pagination: PaginationInput
  ) {
    People(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...SelectUsersDialog_UserThin
      }
    }
  }
  fragment SelectUsersDialog_UserThin on Employee {
    id
    name
    profileImage {
      ...AvatarProfileImage
    }
    locations {
      id
      name
    }
    roles {
      id
      name
    }
  }
  query SelectUsersDialog_LocationGroups($type: LocationGroupType!) {
    LocationGroups(input: { types: [$type] }) {
      objects {
        ...SelectUsersDialog_LocationGroup
      }
    }
  }
  fragment SelectUsersDialog_LocationGroup on LocationGroup {
    id
    name
  }
`;

export default SelectUsersDialog;
